import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";

// Redirect www to non-www
if (
  window.location.hostname === "www.tawallet.com" &&
  window.location.protocol === "https:"
) {
  window.location.replace(
    "https://tawallet.com" +
      window.location.pathname +
      window.location.search +
      window.location.hash,
  );
}

// ✅ Keys that must NEVER be wiped by a blanket clear() call — but CAN be
// explicitly removed/updated via setItem/removeItem when the app needs to
// (logout, switching numbers, token refresh, etc).
//
// IMPORTANT: we no longer monkey-patch the native localStorage.removeItem /
// localStorage.clear. Patching removeItem to silently block removal of
// SessionWalletToken caused real bugs — explicit, intentional calls like
// `localStorage.removeItem("SessionWalletToken")` (used when switching
// numbers, or clearing a stale token) were turned into silent no-ops,
// leaving an old/expired WalletToken stuck in storage forever. That stale
// token is what produced "Token not found or inactive" and inconsistent
// login behavior.
//
// Use safeLocalStorageClear()/safeSessionStorageClear() everywhere you
// want a "clear everything except these keys" wipe (e.g. logout). Use the
// normal localStorage.removeItem(key) / localStorage.setItem(key, val)
// directly anywhere you need to actually change a protected key on purpose.
const PROTECTED_KEYS = {
  localStorage: ["SessionWalletToken"],
  sessionStorage: ["mobileNumber"],
};

/**
 * Safe clear — snapshots protected keys, clears storage, restores them.
 * Use this everywhere instead of localStorage.clear() / sessionStorage.clear()
 * when you want a blanket wipe but need a couple of keys to survive it.
 */
window.safeLocalStorageClear = () => {
  const saved = {};
  PROTECTED_KEYS.localStorage.forEach((k) => {
    const v = localStorage.getItem(k);
    if (v !== null) saved[k] = v;
  });
  localStorage.clear();
  Object.entries(saved).forEach(([k, v]) => localStorage.setItem(k, v));
};

window.safeSessionStorageClear = () => {
  const saved = {};
  PROTECTED_KEYS.sessionStorage.forEach((k) => {
    const v = sessionStorage.getItem(k);
    if (v !== null) saved[k] = v;
  });
  sessionStorage.clear();
  Object.entries(saved).forEach(([k, v]) => sessionStorage.setItem(k, v));
};

// ✅ Single-tab enforcement
const TAB_KEY = "tawallet_active_tab";
const TAB_ID = Math.random().toString(36).slice(2);

localStorage.setItem(TAB_KEY, TAB_ID);

const tabChannel = new BroadcastChannel("tawallet_tab_channel");
tabChannel.postMessage({ type: "TAB_OPENED", tabId: TAB_ID });

tabChannel.onmessage = (event) => {
  if (event.data.type === "TAB_OPENED" && event.data.tabId !== TAB_ID) {
    showDuplicateTabOverlay();
    tabChannel.close();
  }
};

window.addEventListener("beforeunload", () => {
  // Only remove the tab key — SessionWalletToken is untouched, since we
  // simply don't call removeItem/clear on it here.
  if (localStorage.getItem(TAB_KEY) === TAB_ID) {
    localStorage.removeItem(TAB_KEY);
  }
  tabChannel.close();
});

function showDuplicateTabOverlay() {
  const existing = document.getElementById("duplicate-tab-overlay");
  if (existing) return;

  const overlay = document.createElement("div");
  overlay.id = "duplicate-tab-overlay";
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 999999;
    backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    background: rgba(0,0,0,0.55); display: flex;
    align-items: center; justify-content: center; pointer-events: all;
  `;
  overlay.addEventListener("click", (e) => e.stopPropagation());
  overlay.addEventListener("keydown", (e) => e.stopPropagation());

  overlay.innerHTML = `
    <div style="background:#fff;border-radius:16px;padding:40px 36px;max-width:420px;
      width:90%;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,0.3);
      font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;">
      <div style="font-size:48px;margin-bottom:16px;">🔒</div>
      <h2 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1a202c;">
        Session Active in Another Tab</h2>
      <p style="color:#718096;font-size:14px;line-height:1.6;margin:0 0 24px;">
        TAWallet is already open in another browser tab.<br/>
        For your security, only one tab is allowed at a time.</p>
      <button id="use-this-tab-btn"
        style="background:#3182ce;color:#fff;border:none;border-radius:8px;
        padding:12px 28px;font-size:15px;font-weight:600;cursor:pointer;width:100%;">
        Use TAWallet in This Tab</button>
      <p style="margin:14px 0 0;font-size:12px;color:#a0aec0;">
        Or close this tab and return to the existing session.</p>
    </div>
  `;

  document.body.appendChild(overlay);
  document.body.style.overflow = "hidden";

  // ✅ Attach click handler via JS (not inline onclick)
  document.getElementById("use-this-tab-btn").addEventListener("click", () => {
    // Only remove the tab lock
    localStorage.removeItem(TAB_KEY);
    window.location.reload();
  });
}

// ✅ Auto-show/hide global loader on fetch
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  window.showLoading?.();
  try {
    const response = await originalFetch(...args);
    return response;
  } finally {
    window.hideLoading?.();
  }
};

// ✅ Auto-show/hide global loader on XHR
const originalXHRSend = XMLHttpRequest.prototype.send;
XMLHttpRequest.prototype.send = function (...args) {
  window.showLoading?.();
  this.addEventListener("loadend", () => {
    window.hideLoading?.();
  });
  return originalXHRSend.apply(this, args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

reportWebVitals();